import { useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { RootState } from "app/store";
import { useScale } from "common/utils";
import { UserPlan } from "features/user/UserPlan";
import { UserStock } from "features/user/UserStock";
import { StockTabIndex } from "features/creator/types";
import { UserMessage } from "features/user/userMessage";
import { UserProfile } from "features/user/UserProfile";
import { usePlan, PlanType } from "features/subscription";
import { getUserStatus, getUserHistory } from "features/user/api";
import { selectAllChatRoomMessageSync } from "features/user/slice";
import { ProfileTab, AVAILABLE_UPGRADE_PLANS } from "features/user/constants";
import { UserProgressCourseChart } from "features/user/UserProgressCourseChart";
import { UserProgressCreatorChart } from "features/user/UserProgressCreatorChart";
import { UserProgressCourseCleared } from "features/user/UserProgressCourseCleared";
import { UserProgressCreatorProject } from "features/user/UserProgressCreatorProject";
import { UserBottomTabBar } from "./UserBottomTabBar";
import { UserHeader, HEADER_HEIGHT } from "./UserHeader";
import { PlanSegmentControl } from "./PlanSegmentControl";
import { StockSegmentControl } from "./StockSegmentControl";
import { ProfileSegmentControl } from "./ProfileSegmentControl";
import { ProgressSegmentControl } from "./ProgressSegmentControl";
import { UserPageTab, ProgressSubTab, PlanSubTab } from "./UserPageTab";
import { UserSideActions, SIDE_ACTIONS_BASE_WIDTH } from "./UserSideActions";

// ベース解像度 (BASE_WIDTH=1280, BASE_HEIGHT=960) からメインカード設計サイズを逆算
// コンテンツエリア左右パディング 16*2=32、メインカード border 6*2=12
const CONTENT_PADDING_X = 16 * 2;
const CONTENT_PADDING_BOTTOM = 16;
const CARD_BORDER = 6 * 2;
const SIDE_GAP = 16;
const TAB_BAR_MARGIN = 16;
const TAB_BAR_HEIGHT_BASE = 142;
const MAIN_CARD_BASE_WIDTH_FULL = 1280 - CONTENT_PADDING_X - CARD_BORDER; // 1240
const MAIN_CARD_BASE_WIDTH_WITH_SIDE =
  MAIN_CARD_BASE_WIDTH_FULL - SIDE_ACTIONS_BASE_WIDTH - SIDE_GAP; // 1096

export { UserPageTab };

export const UserFullPage = () => {
  const { scale, width, height } = useScale();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const active = useSelector((state: RootState) => state.user.active);
  const chatRoomMessageSync = useSelector(selectAllChatRoomMessageSync);

  const tabParam = searchParams.get("tab");
  const initialTab = Object.values(UserPageTab).includes(tabParam as UserPageTab)
    ? (tabParam as UserPageTab)
    : UserPageTab.PROFILE;

  const subTabParam = searchParams.get("subTab");
  const initialPlanSubTab = Object.values(PlanSubTab).includes(subTabParam as PlanSubTab)
    ? (subTabParam as PlanSubTab)
    : PlanSubTab.CURRENT;

  const [activeTab, setActiveTab] = useState<UserPageTab>(initialTab);
  const [progressTab, setProgressTab] = useState<ProgressSubTab>(ProgressSubTab.COURSE_CHART);
  const [stockTab, setStockTab] = useState<StockTabIndex>(StockTabIndex.ALBUM);
  const [profileTab, setProfileTab] = useState<ProfileTab>(ProfileTab.BASIC);
  const [planTab, setPlanTab] = useState<PlanSubTab>(initialPlanSubTab);
  const currentPlan = usePlan();
  const planShowPurchase = (AVAILABLE_UPGRADE_PLANS[currentPlan] ?? []).length > 0;
  const showPlanTab = currentPlan !== PlanType.BUILDER && currentPlan !== PlanType.EDUCATION;

  useEffect(() => {
    setActiveTab(initialTab);
  }, [active?.uid, initialTab]);

  useEffect(() => {
    if (!showPlanTab && activeTab === UserPageTab.PLAN) {
      setActiveTab(UserPageTab.PROFILE);
    }
  }, [showPlanTab, activeTab]);

  // ユーザー操作でタブ/サブタブが切り替わった際、URL に残った旧パラメータをクリア
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (window.location.search) {
      window.history.replaceState({}, "", "/user");
    }
  }, [activeTab, progressTab, stockTab, profileTab, planTab]);

  useEffect(() => {
    dispatch(getUserStatus({ uid: active.uid }));
    dispatch(getUserHistory({ uid: active.uid }));
  }, [active.uid, dispatch]);

  const hasUnreadMessage =
    chatRoomMessageSync.filter(
      (sync) =>
        sync.uid === active?.uid &&
        (dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
          (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null)),
    ).length > 0;

  // 現在タブが右サイドアクションを持つか
  const hasSideActions =
    activeTab === UserPageTab.PROGRESS ||
    activeTab === UserPageTab.STOCK ||
    activeTab === UserPageTab.PROFILE ||
    (activeTab === UserPageTab.PLAN && planShowPurchase);

  return (
    <div className="flex-col-center relative w-full h-full overflow-hidden bg-[#fff8ec]">
      {/* 上部ヘッダー領域 (左上 UserBtn / 右上 UniversalBtn の間に配置) */}
      <UserHeader />

      {/* コンテンツ領域: サイドアクション有/無でカード幅が動的に変化 */}
      <div
        className="flex-row-el overflow-hidden w-full relative z-[1]"
        style={{
          height: height - (HEADER_HEIGHT + TAB_BAR_HEIGHT_BASE + TAB_BAR_MARGIN) * scale,
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            width: width / scale,
            height:
              (height - (HEADER_HEIGHT + TAB_BAR_HEIGHT_BASE + TAB_BAR_MARGIN) * scale) / scale,
          }}
          className="flex-row-el shrink-0 p-4 overflow-hidden origin-top-left"
        >
          {/* drop-shadow で L 字形状（メイン全高 + パネル content-height）の輪郭に沿って 1 本の影 */}
          <div
            className="flex-row-el w-full h-full"
            style={{
              filter: "drop-shadow(0 4px 12px rgba(63,74,97,0.08))",
            }}
          >
            <div
              className={`flex-col-el flex-1 h-full bg-white overflow-hidden ${
                hasSideActions ? "rounded-l-2xl rounded-br-2xl" : "rounded-2xl"
              }`}
            >
              <div
                className="flex-col-el w-full h-full"
                style={{ display: activeTab === UserPageTab.PROGRESS ? "flex" : "none" }}
              >
                <div className="flex-col-el flex-1 min-h-0 relative overflow-hidden">
                  <UserProgressCourseChart selected={progressTab === ProgressSubTab.COURSE_CHART} />
                  <UserProgressCourseCleared
                    selected={progressTab === ProgressSubTab.COURSE_CLEARED}
                  />
                  <UserProgressCreatorChart
                    selected={progressTab === ProgressSubTab.CREATOR_CHART}
                  />
                  <UserProgressCreatorProject
                    selected={progressTab === ProgressSubTab.CREATOR_PROJECT}
                    close={() => {}}
                  />
                </div>
              </div>

              <div
                className="flex-col-view w-full h-full"
                style={{ display: activeTab === UserPageTab.PROFILE ? "flex" : "none" }}
              >
                <UserProfile selected={activeTab === UserPageTab.PROFILE} activeTab={profileTab} />
              </div>

              <div
                className="flex-col-view w-full h-full"
                style={{ display: activeTab === UserPageTab.STOCK ? "flex" : "none" }}
              >
                <UserStock selected={activeTab === UserPageTab.STOCK} tab={stockTab} />
              </div>

              <div
                className="flex-col-view w-full h-full"
                style={{ display: activeTab === UserPageTab.MESSAGE ? "flex" : "none" }}
              >
                <UserMessage selected={activeTab === UserPageTab.MESSAGE} />
              </div>

              <div
                className="flex-col-view w-full h-full"
                style={{ display: activeTab === UserPageTab.PLAN ? "flex" : "none" }}
              >
                <UserPlan subTab={planTab} />
              </div>
            </div>

            {hasSideActions && (
              <UserSideActions>
                {activeTab === UserPageTab.PROGRESS && (
                  <ProgressSegmentControl value={progressTab} onChange={setProgressTab} />
                )}
                {activeTab === UserPageTab.STOCK && (
                  <StockSegmentControl value={stockTab} onChange={setStockTab} />
                )}
                {activeTab === UserPageTab.PROFILE && (
                  <ProfileSegmentControl value={profileTab} onChange={setProfileTab} />
                )}
                {activeTab === UserPageTab.PLAN && (
                  <PlanSegmentControl
                    value={planTab}
                    onChange={setPlanTab}
                    showPurchase={planShowPurchase}
                  />
                )}
              </UserSideActions>
            )}
          </div>
        </div>
      </div>

      <UserBottomTabBar
        activeTab={activeTab}
        hasUnreadMessage={hasUnreadMessage}
        showPlanTab={showPlanTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};
